import React from "react";

interface RouteTariffBreakdownProps {
  expanded: boolean;
  onToggleExpanded: () => void;
  routeTariffCostPresent?: boolean;
  routeProductCost?: number | null;
  routeShipmentCost?: number | null;
  routeTariffCost?: number | null;
  routeTotalCost?: number | null;
}

const formatAmount = (n: number) =>
  `$${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const RouteTariffBreakdown = ({
  expanded,
  onToggleExpanded,
  routeTariffCostPresent,
  routeProductCost,
  routeShipmentCost,
  routeTariffCost,
  routeTotalCost,
}: RouteTariffBreakdownProps) => {
  if (!routeTariffCostPresent) return null;

  const productCost = routeProductCost ?? 0;
  const shipmentCost = routeShipmentCost ?? 0;
  const tariffCost = routeTariffCost ?? 0;
  const totalLandedCost = routeTotalCost ?? (productCost + shipmentCost + tariffCost);

  return (
    <div data-tutorial-step="route-tariff-breakdown" style={{ border: "1px solid #EAEAEA", borderRadius: 12, overflow: "hidden" }}>
      <button
        onClick={onToggleExpanded}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 20px",
          background: expanded ? "#F9FAFB" : "#fff",
          border: "none",
          cursor: "pointer",
          transition: "background 0.15s",
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 600, color: "#000" }}>Tariff Breakdown</span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#6B7280"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {expanded && (
        <div
          style={{
            padding: 20,
            borderTop: "1px solid #EAEAEA",
            backgroundColor: "#fff",
            display: "flex",
            flexDirection: "column",
            gap: 18,
          }}
        >
          <div
            style={{
              border: "1px solid #DDE5F0",
              borderRadius: 12,
              padding: 20,
              backgroundColor: "#fff",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: "#1F3B73" }}>Total Tariff Impact</span>
              <span style={{ fontSize: 18, fontWeight: 700, color: "#EF2D3E" }}>{formatAmount(tariffCost)}</span>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: 14, color: "#374151" }}>Product Cost</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>{formatAmount(productCost)}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: 14, color: "#374151" }}>Shipment Cost</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>{formatAmount(shipmentCost)}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: 14, color: "#374151" }}>Tariff Cost</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: "#EF2D3E" }}>{formatAmount(tariffCost)}</span>
              </div>
            </div>

            <div style={{ margin: "14px 0", borderTop: "1px solid #E5E7EB" }} />

            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 15, fontWeight: 600, color: "#1F3B73" }}>Total Landed Cost</span>
              <span style={{ fontSize: 17, fontWeight: 700, color: "#000" }}>{formatAmount(totalLandedCost)}</span>
            </div>
          </div>

          <div
            style={{
              backgroundColor: "#EFF6FF",
              border: "1px solid #BFDBFE",
              borderRadius: 12,
              padding: 16,
              fontSize: 14,
              color: "#1E3A8A",
              lineHeight: 1.6,
            }}
          >
            <span style={{ fontWeight: 700 }}>Note:</span> Tariff rates are subject to change based on trade agreements and government policies. Current rates shown are estimates.
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteTariffBreakdown;
